import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  PaginationState,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Spinner } from "src/core/components/custom-ui/spinner";
import { FoldersListFilters } from "src/core/components/folders-list/FoldersListFilters";
import { Pagination } from "src/core/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/core/components/ui/table";
import { Brand } from "src/core/constants/brand";
import { Status } from "src/core/constants/folders";
import { FolderListOf } from "src/core/types/folderListStrategy";

type FoldersListDataTableProps = {
  columns: ColumnDef<FolderListOf<Brand>>[];
  data: FolderListOf<Brand>[];
  icon: ComponentType;
  isLoading?: boolean;
  noResultText?: string;
  sorting: SortingState;
  onSortingChange: (updater: SortingState | ((old: SortingState) => SortingState)) => void;
  pagination: PaginationState;
  onPaginationChange: (
    updater: PaginationState | ((old: PaginationState) => PaginationState)
  ) => void;
  totalCount: number;
  status?: Status;
  buildFolderPath: (args: { id: string; status?: Status }) => string;
};

export const FoldersListDataTable = ({
  columns,
  data,
  icon: Icon,
  isLoading = false,
  noResultText,
  sorting,
  onSortingChange,
  pagination,
  onPaginationChange,
  totalCount,
  status,
  buildFolderPath,
}: FoldersListDataTableProps) => {
  const { t } = useTranslation();

  const loading = isLoading;

  const table = useReactTable({
    data,
    columns,
    state: { sorting, pagination },
    onSortingChange,
    onPaginationChange,
    manualSorting: true,
    manualPagination: true,
    pageCount: Math.ceil(totalCount / (pagination.pageSize > 0 ? pagination.pageSize : 1)),
    getCoreRowModel: getCoreRowModel(),
  });

  const hasRows = table.getRowModel().rows?.length > 0;

  return (
    <div className="folders-list-wrapper" aria-busy={loading}>
      <FoldersListFilters status={status} disabled={loading} />

      {loading ? (
        <Table className="folders-list-table" style={{ tableLayout: "fixed", width: "100%" }}>
          <TableBody>
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-24 text-center rounded-tl-lg rounded-tr-lg"
              >
                <div className="flex items-center justify-center">
                  <Spinner size="lg" />
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      ) : hasRows ? (
        <div className="relative">
          <div className={loading ? "pointer-events-none opacity-60" : undefined}>
            <Table className="folders-list-table" style={{ tableLayout: "fixed", width: "100%" }}>
              <TableHeader className="folders-list-header">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const canSort = header.column.getCanSort() && !loading;

                      return (
                        <TableHead
                          key={header.id}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                          className={canSort ? "cursor-pointer" : "cursor-default"}
                        >
                          {header.isPlaceholder ? null : (
                            <>
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {header.column.getCanSort() && (
                                <span style={{ marginLeft: 4, color: "#bbb" }}>
                                  {header.column.getIsSorted() === "desc" ? (
                                    <b style={{ color: "#333" }}>▼</b>
                                  ) : header.column.getIsSorted() === "asc" ? (
                                    <b style={{ color: "#333" }}>▲</b>
                                  ) : (
                                    "▼▲"
                                  )}
                                </span>
                              )}
                            </>
                          )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>

              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    className="folders-list-table-row cursor-pointer"
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    data-testid="folders-list-row"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell className="folders-list-table-cell" key={cell.id}>
                        <Link
                          to={buildFolderPath({ id: row.original.id, status })}
                          className="block w-full h-full no-underline text-inherit"
                          aria-disabled={loading}
                          tabIndex={loading ? -1 : 0}
                          onClick={(e) => {
                            if (loading) e.preventDefault();
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Link>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/40">
              <Spinner size="lg" />
            </div>
          )}
        </div>
      ) : (
        <div className="folders-list-no-result">
          <div className="flex flex-col items-center justify-center gap-y-10">
            <Icon />
            {noResultText ? t(noResultText) : t("folders.list.noResult")}
          </div>
        </div>
      )}

      {!loading && hasRows && (
        <Pagination
          currentPage={pagination.pageIndex}
          totalPages={table.getPageCount()}
          onPageChange={(page) => onPaginationChange({ ...pagination, pageIndex: page })}
          pageSize={pagination.pageSize}
          onPageSizeChange={(pageSize) =>
            onPaginationChange({ ...pagination, pageSize, pageIndex: 0 })
          }
          totalCount={totalCount}
        />
      )}
    </div>
  );
};
